import { useEffect, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import AppConfig from '~/config/AppConfig';
import useQueryAgent from '~/hooks/useQueryAgent';
import { transformDemandOverview } from '../utils/demandOverview/transformDemandOverview';

const demandOverviewConfig = AppConfig.secureAdServer.demandOverview;

function useDemandOverview(activeTabId) {
  const { get } = useQueryAgent();
  const { enqueueSnackbar } = useSnackbar();
  const lastErrorKeyRef = useRef('');
  const endpoint = demandOverviewConfig.endpoints[activeTabId];

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['secureAdServer', 'demandOverview', activeTabId],
    queryFn: async () => {
      const { data } = await get([endpoint, { limit: -1 }]);
      return data;
    },
    enabled: Boolean(endpoint),
    staleTime: demandOverviewConfig.cache.staleTime,
    gcTime: demandOverviewConfig.cache.gcTime,
    refetchOnWindowFocus: true,
  });

  const groups = useMemo(() => transformDemandOverview(activeTabId, data), [activeTabId, data]);

  useEffect(() => {
    if (!error) {
      lastErrorKeyRef.current = '';
      return;
    }

    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Something went wrong while fetching demand overview data';
    const errorKey = `${activeTabId}:${message}`;

    if (lastErrorKeyRef.current === errorKey) {
      return;
    }

    enqueueSnackbar(message, { variant: 'error' });
    lastErrorKeyRef.current = errorKey;
  }, [activeTabId, enqueueSnackbar, error]);

  return {
    groups,
    isLoading,
    isFetching,
    error,
  };
}

export default useDemandOverview;
